import type { ReactNode } from "react"
import {
	IconAtom2Outline18 as AtomIcon,
	IconChevronDownOutline18 as ChevronDown,
	IconXmarkOutline18 as Dismiss,
	IconLink3Outline18 as LinkIcon,
	IconPlusOutline18 as Plus,
	IconCaretUpOutline18 as Upvote,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	Badge,
	Button,
	IconButton,
	IssueStatusIcon,
	LinearIconThemed,
	ProjectStatusIcon,
} from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

export interface Suggestion {
	kind: "issue" | "project"
	identifier?: string
	title: string
	status: string
	requests: number
	reason: string
}

export interface CardProps {
	portalName: string
	request: string
	suggestions: Suggestion[]
}

export const SAMPLE: CardProps = {
	portalName: "Partners",
	request:
		"Can we export a dashboard as CSV? We rebuild it in Sheets every Monday.",
	suggestions: [
		{
			kind: "issue",
			identifier: "PRO-412",
			title: "Export dashboards to CSV",
			status: "unstarted",
			requests: 14,
			reason: "Same ask, 14 other customers",
		},
		{
			kind: "project",
			title: "Reporting exports",
			status: "started",
			requests: 31,
			reason: "Project covering all exports",
		},
		{
			kind: "issue",
			identifier: "PRO-388",
			title: "Scheduled email reports",
			status: "backlog",
			requests: 6,
			reason: "Related: weekly report delivery",
		},
	],
}

const CARD =
	"rounded-lg bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10"

function SuggestionMark({ s }: { s: Suggestion }) {
	return s.kind === "issue" ? (
		<IssueStatusIcon status={s.status} className="h-3.5 w-3.5" />
	) : (
		<ProjectStatusIcon
			status={s.status as "started"}
			colored
			className="h-3.5 w-3.5"
		/>
	)
}

function SuggestionTitle({
	s,
	className,
}: {
	s: Suggestion
	className?: string
}) {
	return (
		<div className={cn("flex min-w-0 items-center gap-1.5", className)}>
			<SuggestionMark s={s} />
			{s.identifier && (
				<span className="flex-none text-xs text-tertiary">{s.identifier}</span>
			)}
			<span className="truncate text-xs font-medium text-primary">
				{s.title}
			</span>
		</div>
	)
}

function RequestCount({ n }: { n: number }) {
	return (
		<Badge variant="transparent" size="sm" className="flex-none gap-0.5 px-0">
			<Upvote className="h-3 w-3 flex-none" />
			{n}
		</Badge>
	)
}

function PortalMark({ name }: { name: string }) {
	return (
		<span className="flex min-w-0 items-center gap-1.5 text-xs text-secondary">
			<Avatar name={name} roundness="sm" size="xxs" className="flex-none" />
			<span className="truncate">{name} portal</span>
		</span>
	)
}

// 1. One best match, one primary action. The other two hide behind a link.
export function CardTopMatch({ portalName, suggestions }: CardProps) {
	const top = suggestions[0]
	if (!top) return null
	return (
		<div className={cn(CARD, "p-3")}>
			<div className="flex items-center justify-between gap-2">
				<span className="text-xs font-medium text-secondary">
					Request from the {portalName} portal
				</span>
				<IconButton
					variant="ghost"
					size="md"
					Icon={Dismiss}
					aria-label="Dismiss"
				/>
			</div>
			<div className="mt-2.5 rounded-md bg-gray-50 p-2 dark:bg-gray-800/60">
				<SuggestionTitle s={top} />
				<div className="mt-1 flex items-center justify-between gap-2">
					<span className="truncate text-xxs text-tertiary">{top.reason}</span>
					<RequestCount n={top.requests} />
				</div>
			</div>
			<div className="mt-2.5 flex items-center gap-1.5">
				<Button Icon={LinkIcon} size="sm">
					Link {top.identifier ?? "project"}
				</Button>
				<Button variant="ghost" size="sm" Icon={Plus}>
					Create issue
				</Button>
			</div>
			<button
				type="button"
				className="mt-2 text-xxs text-tertiary hover:text-secondary"
			>
				{suggestions.length - 1} other matches
			</button>
		</div>
	)
}

// 2. Ranked list. Every match links in one click; create sits in the footer.
export function CardRankedList({ portalName, suggestions }: CardProps) {
	return (
		<div className={cn(CARD, "overflow-hidden")}>
			<div className="flex items-center justify-between gap-2 px-3 pb-1.5 pt-2.5">
				<span className="text-xs font-medium text-primary">
					Link this request
				</span>
				<PortalMark name={portalName} />
			</div>
			<div className="flex flex-col px-1 pb-1">
				{suggestions.map((s) => (
					<div
						key={s.title}
						className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<div className="min-w-0 flex-1">
							<SuggestionTitle s={s} />
							<div className="mt-0.5 truncate pl-5 text-xxs text-tertiary">
								{s.reason}
							</div>
						</div>
						<Button
							variant="outline"
							size="xs"
							className="opacity-0 group-hover:opacity-100"
						>
							Link
						</Button>
					</div>
				))}
			</div>
			<div className="flex items-center gap-1 border-t border-gray-200 px-2 py-1.5 dark:border-gray-700/80">
				<Button variant="ghost" size="sm" Icon={Plus}>
					New issue
				</Button>
				<Button variant="ghost" size="sm" Icon={Plus}>
					New project
				</Button>
			</div>
		</div>
	)
}

// 3. Create first. The customer's words become the draft; a match is the fallback.
export function CardDraftFirst({
	portalName,
	request,
	suggestions,
}: CardProps) {
	const top = suggestions[0]
	return (
		<div className={cn(CARD, "p-3")}>
			<div className="flex items-center gap-1.5 text-xs font-medium text-secondary">
				<AtomIcon className="h-3.5 w-3.5 flex-none text-tertiary" />
				Drafted from this request
			</div>
			<div className="mt-2 rounded-md p-2 ring-1 ring-gray-200 dark:ring-gray-700/80">
				<div className="text-xs font-medium text-primary">
					Export dashboards as CSV
				</div>
				<div className="mt-1 line-clamp-2 text-xxs text-tertiary">
					{request}
				</div>
				<div className="mt-2 flex items-center gap-1.5">
					<Button variant="outline-subtle" size="xs" iconRight={ChevronDown}>
						Issue
					</Button>
					<Button
						variant="outline-subtle"
						size="xs"
						Icon={LinearIconThemed}
						iconRight={ChevronDown}
					>
						Product
					</Button>
				</div>
			</div>
			<Button size="sm" className="mt-2.5 w-full max-w-none">
				Create in Linear
			</Button>
			{top && (
				<div className="mt-2.5 flex items-center gap-1.5 text-xxs text-tertiary">
					<span className="flex-none">Or link</span>
					<button
						type="button"
						className="min-w-0 truncate font-medium text-secondary hover:text-primary"
					>
						{top.identifier} {top.title}
					</button>
					<span className="flex-none">({top.requests} requests)</span>
				</div>
			)}
			<div className="mt-1 text-xxs text-tertiary">
				From the {portalName} portal
			</div>
		</div>
	)
}

// 4. No card at all: a suggestion row inside the Links group, where links already live.
export function CardInlineLinks({ suggestions }: CardProps) {
	return (
		<div className="flex flex-col gap-1">
			<div className="px-2 text-xs font-medium text-tertiary">Links</div>
			{suggestions.slice(0, 2).map((s) => (
				<div
					key={s.title}
					className="group flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800"
				>
					<SuggestionTitle s={s} className="flex-1 opacity-60" />
					<Badge variant="light-gray" size="sm" className="flex-none">
						Suggested
					</Badge>
					<IconButton
						variant="ghost"
						size="md"
						Icon={LinkIcon}
						aria-label="Link"
					/>
				</div>
			))}
			<div className="mt-1 flex gap-2 px-2">
				<Button variant="ghost" size="sm" Icon={Plus}>
					Create issue
				</Button>
				<Button variant="ghost" size="sm" Icon={LinkIcon}>
					Link other
				</Button>
			</div>
		</div>
	)
}

// 5. Context first: the quote and the portal, then what it matches. Linking also
// says which portal the item shows up on, since linking adds the thread's portal.
export function CardWithContext({
	portalName,
	request,
	suggestions,
}: CardProps) {
	const top = suggestions[0]
	if (!top) return null
	return (
		<div className={cn(CARD, "overflow-hidden")}>
			<div className="p-3">
				<div className="flex items-center justify-between gap-2">
					<PortalMark name={portalName} />
					<Badge variant="default" size="sm" className="flex-none">
						Not linked
					</Badge>
				</div>
				<p className="mt-2 border-l-2 border-gray-200 pl-2 text-xs text-secondary dark:border-gray-700/80">
					{request}
				</p>
			</div>
			<div className="border-t border-gray-200 bg-gray-50 p-3 dark:border-gray-700/80 dark:bg-gray-800/40">
				<div className="text-xxs font-medium uppercase tracking-wide text-tertiary">
					Best match
				</div>
				<div className="mt-1.5 flex items-center justify-between gap-2">
					<SuggestionTitle s={top} />
					<RequestCount n={top.requests} />
				</div>
				<div className="mt-1 text-xxs text-tertiary">
					Linking shows it on the {portalName} portal.
				</div>
				<div className="mt-2.5 flex items-center gap-1.5">
					<Button size="sm" Icon={LinkIcon}>
						Link
					</Button>
					<Button variant="outline" size="sm" iconRight={ChevronDown}>
						Create
					</Button>
				</div>
			</div>
		</div>
	)
}

function SidebarFrame({ children }: { children: ReactNode }) {
	return (
		<div className="w-[300px] rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700/80 dark:bg-gray-900">
			<div className="mb-3 flex items-center gap-2 px-1 text-xs text-tertiary">
				<Avatar name="Dana Weiss" size="xxs" className="flex-none" />
				Dana Weiss, Northwind
			</div>
			{children}
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="w-[300px] space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="min-h-[48px] text-xxs text-tertiary">{note}</div>
			<SidebarFrame>{children}</SidebarFrame>
		</div>
	)
}

export function FeatureRequestLinkCardPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen bg-white p-10 dark:bg-gray-950">
			<div className="mb-8 max-w-2xl">
				<div className="text-lg font-semibold text-primary">
					Unlinked feature request card, five options
				</div>
				<div className="mt-1 text-sm text-secondary">
					Shows in the thread sidebar when a thread came from the feature
					requests portal (origin PORTAL or UPVOTE) and has no issue or project
					yet. Gone the moment one is linked.
				</div>
			</div>
			<div className="flex flex-wrap gap-8">
				<Variant
					label="1. Top match"
					note="One suggestion, one button. Fastest when the match is right, and most of the time it is."
				>
					<CardTopMatch {...SAMPLE} />
				</Variant>
				<Variant
					label="2. Ranked list"
					note="Three matches with the reason for each. Link appears on hover; new issue and new project sit side by side."
				>
					<CardRankedList {...SAMPLE} />
				</Variant>
				<Variant
					label="3. Draft first"
					note="AI writes the issue title from the customer's words. Team and type are one click to change. A match is the quiet fallback."
				>
					<CardDraftFirst {...SAMPLE} />
				</Variant>
				<Variant
					label="4. Inside Links"
					note="No new card. Faded suggested rows in the Links group, so the sidebar keeps its shape."
				>
					<CardInlineLinks {...SAMPLE} />
				</Variant>
				<Variant
					label="5. Quote and match"
					note="The customer's ask on top, the match below, and the portal the link will publish to."
				>
					<CardWithContext {...SAMPLE} />
				</Variant>
			</div>
		</div>
	)
}
